import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  getPreferences,
  updatePreferences,
  subscribeDevice,
  unsubscribeDevice,
  getDevices,
  removeDevice,
  iosInstallNeeded,
} from '../services/pushService';

const BASE = process.env.REACT_APP_API_URL || '';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

export default function NotificationPreferencesPage() {
  const [prefs, setPrefs]       = useState(null);
  const [devices, setDevices]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [subBusy, setSubBusy]   = useState(false);
  const [error, setError]       = useState(null);
  const [saved, setSaved]       = useState(false);
  const [testBusy, setTestBusy] = useState(false);
  const [testResult, setTestResult] = useState(null);
  // Whether THIS browser/device currently holds a real local push subscription — checked against
  // the browser's own PushManager, not the account-wide server flag (which used to be misread as
  // "this device" even when only some other device had ever subscribed).
  const [deviceSubscribed, setDeviceSubscribed] = useState(false);

  const supported = 'serviceWorker' in navigator && 'PushManager' in window;
  const needsIOSInstall = iosInstallNeeded();

  const checkThisDevice = useCallback(async () => {
    if (!supported) { setDeviceSubscribed(false); return; }
    try {
      const reg = await navigator.serviceWorker.getRegistration('/sw.js');
      const sub = reg && await reg.pushManager.getSubscription();
      setDeviceSubscribed(!!sub);
    } catch { setDeviceSubscribed(false); }
  }, [supported]);

  const load = useCallback(async () => {
    try {
      const [p, d] = await Promise.all([getPreferences(), getDevices()]);
      setPrefs(p);
      setDevices(d);
    } catch {
      setError('Failed to load notification preferences.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); checkThisDevice(); }, [load, checkThisDevice]);

  // ── Subscribe / Unsubscribe this device ────────────────────────────────────

  const handleSubscribe = async () => {
    if (!supported) return;
    setSubBusy(true);
    setError(null);
    try {
      const granted = await Notification.requestPermission();
      if (granted !== 'granted') {
        setError('Notification permission denied. Please allow notifications in your browser settings.');
        return;
      }
      const ok = await subscribeDevice();
      if (ok === 'ios_not_standalone') {
        // Handled by the iOS install banner — nothing to do here
        return;
      }
      if (ok) {
        await load();
        await checkThisDevice();
      } else {
        setError('Could not subscribe this device. Try again.');
      }
    } finally {
      setSubBusy(false);
    }
  };

  const handleUnsubscribe = async () => {
    setSubBusy(true);
    setError(null);
    try {
      await unsubscribeDevice();
      await load();
      await checkThisDevice();
    } finally {
      setSubBusy(false);
    }
  };

  // ── Save master toggle + per-event toggles ─────────────────────────────────

  const handleMasterToggle = async (enabled) => {
    setSaving(true);
    try {
      await updatePreferences({ enabled });
      setPrefs(p => ({ ...p, enabled }));
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {
      setError('Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const handleEventToggle = async (key, value) => {
    const newEvents = { ...(prefs.events || {}), [key]: value };
    setSaving(true);
    try {
      await updatePreferences({ events: newEvents });
      setPrefs(p => ({ ...p, events: newEvents }));
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {
      setError('Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const handleTestPush = async () => {
    setTestBusy(true);
    setTestResult(null);
    try {
      const { data } = await axios.post(`${BASE}/api/push/test`, {}, auth());
      setTestResult(data);
    } catch (err) {
      setTestResult({ ok: false, error: err.response?.data?.error || err.message });
    } finally {
      setTestBusy(false);
    }
  };

  const handleRemoveDevice = async (id) => {
    try {
      await removeDevice(id);
      setDevices(d => d.filter(x => x.id !== id));
    } catch {
      setError('Failed to remove device.');
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="container py-4">
        <div className="spinner-border spinner-border-sm" role="status" />
      </div>
    );
  }

  // Group event defs by group label
  const eventDefs = prefs?.eventDefs || {};
  const groups = {};
  Object.entries(eventDefs).forEach(([key, def]) => {
    if (!groups[def.group]) groups[def.group] = [];
    groups[def.group].push({ key, ...def });
  });

  const isSubscribed = deviceSubscribed;

  return (
    <div className="container py-4" style={{ maxWidth: 680 }}>
      <h4 className="mb-1">Push Notifications</h4>
      <p className="text-muted small mb-4">
        Receive real-time alerts on this device for key events.
        {!supported && (
          <span className="text-warning ms-1">
            Your browser does not support push notifications.
          </span>
        )}
      </p>

      {error && (
        <div className="alert alert-danger py-2 small">{error}</div>
      )}
      {saved && (
        <div className="alert alert-success py-2 small">Preferences saved.</div>
      )}

      {/* iOS: must be installed as PWA first */}
      {needsIOSInstall && (
        <div className="alert alert-info mb-4">
          <div className="fw-semibold mb-2">📱 iPhone / iPad — Install Required</div>
          <p className="small mb-2">
            iOS only delivers push notifications to apps installed on your Home Screen.
            Opening QuickLOG in Safari is not enough — you need to add it first.
          </p>
          <ol className="small mb-0 ps-3">
            <li>In Safari, tap the <strong>Share</strong> button <strong>⎙</strong> at the bottom of the screen.</li>
            <li>Scroll down and tap <strong>"Add to Home Screen"</strong>.</li>
            <li>Tap <strong>Add</strong> in the top-right corner.</li>
            <li>Open <strong>QuickLOG from the Home Screen icon</strong> (not from Safari).</li>
            <li>Come back to this page and tap <strong>"Enable on this device"</strong>.</li>
          </ol>
          <div className="mt-2 small text-muted">Requires iOS 16.4 or later.</div>
        </div>
      )}

      {/* Device subscription card */}
      <div className="card mb-4">
        <div className="card-body d-flex align-items-center justify-content-between gap-3 flex-wrap">
          <div>
            <div className="fw-semibold">This device</div>
            <div className="text-muted small">
              {needsIOSInstall
                ? 'Add to Home Screen first (see instructions above).'
                : isSubscribed
                  ? 'Subscribed — notifications will appear on this device.'
                  : 'Not subscribed — you will not receive notifications here.'}
            </div>
          </div>
          {supported && !needsIOSInstall && (
            isSubscribed ? (
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={handleUnsubscribe}
                disabled={subBusy}
              >
                {subBusy ? 'Working…' : 'Unsubscribe'}
              </button>
            ) : (
              <button
                className="btn btn-primary btn-sm"
                onClick={handleSubscribe}
                disabled={subBusy}
              >
                {subBusy ? 'Working…' : 'Enable on this device'}
              </button>
            )
          )}
        </div>
      </div>

      {/* Diagnostic test button */}
      {isSubscribed && (
        <div className="mb-4">
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={handleTestPush}
            disabled={testBusy}
          >
            {testBusy ? 'Sending…' : 'Send test notification'}
          </button>
          {testResult && (
            <div className={`alert py-2 mt-2 ${testResult.ok ? 'alert-success' : 'alert-danger'}`} style={{ fontSize: '0.75rem', fontFamily: 'monospace' }}>
              {testResult.message && <div className="mb-1">{testResult.message}</div>}
              {testResult.error && <div className="mb-1">Error: {testResult.error}</div>}
              {testResult.results?.map((r, i) => (
                <div key={i} className="mb-1">
                  <strong>Sub {r.subId}</strong>{' '}
                  {r.ok
                    ? <span className="text-success">✓ delivered (HTTP {r.statusCode})</span>
                    : <span className="text-danger">✗ HTTP {r.statusCode} — {r.body || r.message}</span>
                  }
                  <div className="text-muted" style={{ fontSize: '0.68rem' }}>{r.endpoint}</div>
                  <div style={{ fontSize: '0.68rem' }} className={(r.p256dhBytes !== 65 || r.authBytes !== 16) ? 'text-danger fw-bold' : 'text-muted'}>
                    keys: p256dh {r.p256dhBytes}B (expect 65), auth {r.authBytes}B (expect 16){(r.p256dhBytes !== 65 || r.authBytes !== 16) ? ' ⚠ CORRUPTED' : ' ✓'}
                  </div>
                </div>
              ))}
              {testResult.jwtDebug && (
                <div className="mt-2 border-top pt-2" style={{ fontSize: '0.68rem', wordBreak: 'break-all' }}>
                  <div><strong>JWT header:</strong> {JSON.stringify(testResult.jwtDebug.header)}</div>
                  <div><strong>JWT claims:</strong> {JSON.stringify(testResult.jwtDebug.claims)}</div>
                  <div><strong>Auth header:</strong> {testResult.jwtDebug.authHeader}</div>
                  {testResult.jwtDebug.error && <div className="text-danger">JWT debug error: {testResult.jwtDebug.error}</div>}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Master on/off */}
      <div className="card mb-4">
        <div className="card-body d-flex align-items-center justify-content-between">
          <div>
            <div className="fw-semibold">All notifications</div>
            <div className="text-muted small">Master switch — turns all push alerts on or off for your account.</div>
          </div>
          <div className="form-check form-switch mb-0">
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              id="masterSwitch"
              checked={prefs?.enabled ?? true}
              onChange={e => handleMasterToggle(e.target.checked)}
              disabled={saving}
            />
          </div>
        </div>
      </div>

      {/* Per-event toggles */}
      {Object.entries(groups).map(([groupName, events]) => (
        <div className="card mb-3" key={groupName}>
          <div className="card-header py-2 small fw-semibold text-muted text-uppercase" style={{ letterSpacing: '0.04em' }}>
            {groupName}
          </div>
          <ul className="list-group list-group-flush">
            {events.map(({ key, label }) => {
              const checked = (prefs?.events || {})[key] !== false;
              return (
                <li key={key} className="list-group-item d-flex align-items-center justify-content-between">
                  <span className="small">{label}</span>
                  <div className="form-check form-switch mb-0">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      checked={checked}
                      onChange={e => handleEventToggle(key, e.target.checked)}
                      disabled={saving || !prefs?.enabled}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      ))}

      {/* Subscribed devices */}
      {devices.length > 0 && (
        <div className="card mt-4">
          <div className="card-header py-2 small fw-semibold text-muted text-uppercase" style={{ letterSpacing: '0.04em' }}>
            Subscribed devices
          </div>
          <ul className="list-group list-group-flush">
            {devices.map(dev => (
              <li key={dev.id} className="list-group-item d-flex align-items-center justify-content-between gap-2">
                <div>
                  <div className="small text-truncate" style={{ maxWidth: 420 }}>
                    {dev.userAgent || 'Unknown browser'}
                  </div>
                  <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                    Added {new Date(dev.createdAt).toLocaleDateString()}
                  </div>
                </div>
                <button
                  className="btn btn-outline-danger btn-sm"
                  onClick={() => handleRemoveDevice(dev.id)}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
